import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { usePendingExpenseApi } from './config/usePendingExpenseApi';
import { parseTableParams } from '../../utils/Utils';
import { updatePaymentStatus } from '../../services/expenseService';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import TextInput from '../../components/ui/TextInput.jsx';
import Button from '../../components/ui/Button';

const Container = styled.div``;

const RejectOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const RejectDialog = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  max-width: 420px;
  width: 90%;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const RejectTitle = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`;

export default function ExpensePendingApprovalsPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const { columns: baseColumns, data, fetching, pagination, approve, reject, refresh } = usePendingExpenseApi(queryParams);

  const handleSetPayment = useCallback(async (row, next) => {
    try {
      await updatePaymentStatus(row.id, next);
      refresh?.();
    } catch { /* interceptor */ }
  }, [refresh]);

  const handleApprove = useCallback(async (row) => {
    await approve(row.id);
  }, [approve]);

  const handleRejectOpen = useCallback((row) => {
    setRejectDialog({ isOpen: true, id: row.id });
    setRejectReason('');
  }, []);

  const handleRejectConfirm = useCallback(async () => {
    if (!rejectReason.trim()) return;
    setProcessing(true);
    await reject(rejectDialog.id, rejectReason);
    setRejectDialog({ isOpen: false, id: null });
    setProcessing(false);
  }, [reject, rejectDialog.id, rejectReason]);

  const handleDetails = useCallback((row) => {
    history.push(`/expenses/${row.id}`);
  }, [history]);

  const handleRowClick = useCallback((row) => {
    
    history.push(`/expenses/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => [
    { type: 'approve', label: t('expense.approve'), onClick: handleApprove },
    { type: 'reject', label: t('expense.reject'), onClick: handleRejectOpen },
    {
      type: 'markAsPaid',
      label: t('expense.markAsPaid'),
      onClick: (row) => handleSetPayment(row, 'paid'),
      disabled: (row) => row?.paymentStatus === 'paid',
    },
    {
      type: 'markAsUnpaid',
      label: t('expense.markAsUnpaid'),
      onClick: (row) => handleSetPayment(row, 'unpaid'),
      disabled: (row) => row?.paymentStatus !== 'paid',
    },
    { type: 'divider' },
    { type: 'details', label: t('expense.detail'), onClick: handleDetails },
  ], [t, handleApprove, handleRejectOpen, handleDetails, handleSetPayment]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      {rejectDialog.isOpen && (
        <RejectOverlay onClick={() => setRejectDialog({ isOpen: false, id: null })}>
          <RejectDialog onClick={(e) => e.stopPropagation()}>
            <RejectTitle>{t('expense.rejectTitle')}</RejectTitle>
            <TextInput
              label={t('expense.rejectReason')}
              type="textarea"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              required
              rows={3}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
              <Button variant="outline" onClick={() => setRejectDialog({ isOpen: false, id: null })} disabled={processing}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleRejectConfirm} disabled={processing || !rejectReason.trim()}
                style={{ background: '#d32f2f' }}>
                {processing ? '...' : t('expense.reject')}
              </Button>
            </div>
          </RejectDialog>
        </RejectOverlay>
      )}

      {useMemo(() => (
        <DataTable
          title={t('expense.pendingApprovals')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
